import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import Swal from 'sweetalert2';

import { SousCategorie } from 'app/models/sous-categorie';
import { SousCategorieService } from 'app/services/sous-categorie/sous-categorie.service';
import { AwscontrolService, ImageUploadResponse } from 'app/services/aws/awscontrol.service';
import { ProduitsService } from 'app/services/produits/produits.service';

type ImageUploadMode = 'direct' | 'imagesousproduit';

@Component({
  selector: 'app-modifier-sous-categorie',
  templateUrl: './modifier-sous-categorie.component.html',
  styleUrls: ['./modifier-sous-categorie.component.scss']
})
export class ModifierSousCategorieComponent implements OnInit {
  private readonly s3BaseUrl = 'https://tunisie-immob.s3.eu-west-3.amazonaws.com/';

  sousCategorie: SousCategorie = new SousCategorie();
  idSouCat: string | null = null;
  produitId: string | null = null;

  // ✅ Language support
  agencyAvailableLanguages: string[] = [];
  showFrenchFields: boolean = false;
  showArabicFields: boolean = false;
  showEnglishFields: boolean = false;
  languageLabels: { [code: string]: string } = { 'FR': 'Français', 'AR': 'العربية', 'EN': 'English' };

  // image_url: only one image (as a key string)
  mainImageKey: string = '';
  uploadingMain = false;

  // listimages: array of full URLs or keys
  secondaryImages: string[] = [];
  uploadingSecondary = false;

  isSubmitting = false;

  // ==================== NEW : MODE + PREVIEW ====================
  mainUploadMode: ImageUploadMode = 'direct';
  mainPreviewOriginalUrl: string | null = null;
  mainPreviewProcessedUrl: string | null = null;
  isAwaitingMainValidation: boolean = false;

  secondaryUploadMode: ImageUploadMode = 'direct';
  secondaryPreviewOriginalUrls: string[] = [];
  secondaryPreviewProcessedUrls: string[] = [];
  isAwaitingSecondaryValidation: boolean = false;
  // =============================================================

  constructor(
    private sousCatService: SousCategorieService,
    private awsService: AwscontrolService,
    private produitService: ProduitsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.idSouCat = this.activatedRoute.snapshot.paramMap.get('id');
    this.getSousCategorieDetails();
  }

  getSousCategorieDetails(): void {
    if (!this.idSouCat) return;
    this.sousCatService.getSousCategorie(this.idSouCat).subscribe({
      next: (data: any) => {
        this.sousCategorie = { ...data } as SousCategorie;
        this.produitId = data.produit_id || data.produitId || null;

        this.sousCategorie.namearabe = data.namearabe || '';
        this.sousCategorie.nameanglais = data.nameanglais || '';
        this.sousCategorie.descriptionarabe = data.descriptionarabe || '';
        this.sousCategorie.descriptionanglais = data.descriptionanglais || '';
        this.sousCategorie.video = data.video || '';

        if (data.image_url) {
          const urls = data.image_url.split(',').map((u: string) => u.trim()).filter((u: string) => u);
          if (urls.length > 0) {
            const first = urls[0];
            this.mainImageKey = first.startsWith('http') ? first.replace(this.s3BaseUrl, '') : first;
          } else {
            this.mainImageKey = '';
          }
        } else {
          this.mainImageKey = '';
        }

        this.secondaryImages = (data.listimages && Array.isArray(data.listimages)) ? [...data.listimages] : [];

        if (this.produitId) {
          this.loadProductLanguages(this.produitId);
        }
      },
      error: () => {
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de charger les détails', confirmButtonColor: '#f44336' });
      }
    });
  }

  private loadProductLanguages(produitId: string): void {
    this.produitService.getProduit(produitId).subscribe({
      next: (product: any) => {
        if (product?.user?.availableLanguages && Array.isArray(product.user.availableLanguages)) {
          this.agencyAvailableLanguages = product.user.availableLanguages.map((l: string) => l.toUpperCase());
        } else {
          this.agencyAvailableLanguages = ['FR'];
        }
        this.showFrenchFields  = this.agencyAvailableLanguages.includes('FR');
        this.showArabicFields  = this.agencyAvailableLanguages.includes('AR');
        this.showEnglishFields = this.agencyAvailableLanguages.includes('EN');
      },
      error: () => {
        this.agencyAvailableLanguages = ['FR'];
        this.showFrenchFields  = true;
        this.showArabicFields  = false;
        this.showEnglishFields = false;
      }
    });
  }

  getFullImageUrl(urlOrKey: string): string {
    if (!urlOrKey) return 'assets/default-subproduct.png';
    if (urlOrKey.startsWith('http')) return urlOrKey;
    return this.s3BaseUrl + urlOrKey;
  }

  // ==================== NEW : helpers ====================
  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;
    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', timer: 2000 });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', timer: 2000 });
      return false;
    }
    return true;
  }

  private fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  // ========================================================

  // ---------- Upload for MAIN image (single) ----------
  onMainFileSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const file = input.files[0];
    if (!file) return;
    if (!this.validateImageFile(file)) { input.value = ''; return; }

    // Aperçu local de l'original
    this.fileToDataUrl(file).then(d => { this.mainPreviewOriginalUrl = d; });

    if (this.mainUploadMode === 'direct') {
      this.uploadMainDirect(file, input);
    } else {
      this.uploadMainViaImagesousproduit(file, input);
    }
  }

  // ---------- Upload direct (comportement original) ----------
  private uploadMainDirect(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.awsService.uploadFile(file, 'sous-categories').subscribe({
      next: (response) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length) {
          const fullUrl = response.urls[0];
          this.mainImageKey = fullUrl.replace(this.s3BaseUrl, '');
          this.mainPreviewOriginalUrl = null;
          Swal.fire({ icon: 'success', title: 'Image mise à jour', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: (err) => {
        this.uploadingMain = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger l\'image' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  // ==================== NEW : main via imagesousproduit ====================
  private uploadMainViaImagesousproduit(file: File, input: HTMLInputElement): void {
    this.uploadingMain = true;
    this.isAwaitingMainValidation = false;
    this.mainPreviewProcessedUrl = null;

    this.awsService.imagesousproduit([file], 'sous-categories').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingMain = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const rawUrl = response.urls[0];
          this.mainPreviewProcessedUrl = rawUrl.startsWith('http') ? rawUrl : this.s3BaseUrl + rawUrl;
          this.isAwaitingMainValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.mainPreviewOriginalUrl = null;
        }
        input.value = '';
      },
      error: () => {
        this.uploadingMain = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter l\'image principale', confirmButtonColor: '#f44336' });
        this.mainPreviewOriginalUrl = null;
        input.value = '';
      }
    });
  }

  setMainUploadMode(mode: ImageUploadMode): void {
    if (this.uploadingMain) return;
    if (this.isAwaitingMainValidation) this.cancelMainPreview(true);
    this.mainUploadMode = mode;
  }

  validateMainPreview(): void {
    if (!this.mainPreviewProcessedUrl) return;
    // On stocke la clé S3 dans mainImageKey
    this.mainImageKey = this.mainPreviewProcessedUrl.replace(this.s3BaseUrl, '');
    this.mainPreviewOriginalUrl = null;
    this.mainPreviewProcessedUrl = null;
    this.isAwaitingMainValidation = false;
    Swal.fire({ icon: 'success', title: 'Image validée', timer: 1500, showConfirmButton: false });
  }

  cancelMainPreview(silent: boolean = false): void {
    const url = this.mainPreviewProcessedUrl;
    const doCancel = () => {
      if (url) this.awsService.deleteFile(url).subscribe({ error: () => {} });
      this.mainPreviewOriginalUrl = null;
      this.mainPreviewProcessedUrl = null;
      this.isAwaitingMainValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser cette image ?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#f44336', cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser', cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }
  // ======================================================================

  // ---------- Upload for SECONDARY images (multiple) ----------
  onSecondaryFilesSelected(event: any): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;

    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (this.validateImageFile(file)) {
        validFiles.push(file);
      } else {
        Swal.fire({ icon: 'warning', title: 'Fichier ignoré', text: `${file.name} invalide`, timer: 2000 });
      }
    }

    if (!validFiles.length) { input.value = ''; return; }

    if (this.secondaryUploadMode === 'direct') {
      this.uploadSecondaryDirect(validFiles, input);
    } else {
      this.secondaryPreviewOriginalUrls = [];
      validFiles.forEach(f => {
        this.fileToDataUrl(f).then(d => { this.secondaryPreviewOriginalUrls.push(d); });
      });
      this.uploadSecondaryViaImagesousproduit(validFiles, input);
    }
  }

  // ---------- Upload direct (comportement original) ----------
  private uploadSecondaryDirect(validFiles: File[], input: HTMLInputElement): void {
    this.uploadingSecondary = true;
    this.awsService.uploadFiles(validFiles, 'sous-categories').subscribe({
      next: (response) => {
        this.uploadingSecondary = false;
        if (response.success && response.urls && response.urls.length) {
          this.secondaryImages.push(...response.urls);
          Swal.fire({ icon: 'success', title: `${response.urls.length} image(s) ajoutée(s)`, timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Upload failed' });
        }
        input.value = '';
      },
      error: (err) => {
        this.uploadingSecondary = false;
        console.error(err);
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger les images' });
        input.value = '';
      }
    });
  }

  // ==================== NEW : secondary via imagesousproduit ====================
  private uploadSecondaryViaImagesousproduit(files: File[], input: HTMLInputElement): void {
    this.uploadingSecondary = true;
    this.isAwaitingSecondaryValidation = false;
    this.secondaryPreviewProcessedUrls = [];

    this.awsService.imagesousproduit(files, 'sous-categories').subscribe({
      next: (response: ImageUploadResponse) => {
        this.uploadingSecondary = false;
        if (response.success && response.urls && response.urls.length > 0) {
          this.secondaryPreviewProcessedUrls = response.urls.map(u => u.startsWith('http') ? u : this.s3BaseUrl + u);
          this.isAwaitingSecondaryValidation = true;
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: (response.errors && response.errors[0]) || 'Échec du traitement', confirmButtonColor: '#f44336' });
          this.secondaryPreviewOriginalUrls = [];
        }
        input.value = '';
      },
      error: () => {
        this.uploadingSecondary = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de traiter les images', confirmButtonColor: '#f44336' });
        this.secondaryPreviewOriginalUrls = [];
        input.value = '';
      }
    });
  }

  setSecondaryUploadMode(mode: ImageUploadMode): void {
    if (this.uploadingSecondary) return;
    if (this.isAwaitingSecondaryValidation) this.cancelSecondaryPreview(true);
    this.secondaryUploadMode = mode;
  }

  validateSecondaryPreview(): void {
    if (this.secondaryPreviewProcessedUrls.length === 0) return;
    this.secondaryImages.push(...this.secondaryPreviewProcessedUrls);
    this.secondaryPreviewOriginalUrls = [];
    this.secondaryPreviewProcessedUrls = [];
    this.isAwaitingSecondaryValidation = false;
    Swal.fire({ icon: 'success', title: 'Images validées', timer: 1500, showConfirmButton: false });
  }

  cancelSecondaryPreview(silent: boolean = false): void {
    const urls = [...this.secondaryPreviewProcessedUrls];
    const doCancel = () => {
      urls.forEach(u => this.awsService.deleteFile(u).subscribe({ error: () => {} }));
      this.secondaryPreviewOriginalUrls = [];
      this.secondaryPreviewProcessedUrls = [];
      this.isAwaitingSecondaryValidation = false;
    };
    if (silent) { doCancel(); return; }
    Swal.fire({
      title: 'Refuser ces images ?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#f44336', cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui, refuser', cancelButtonText: 'Annuler'
    }).then(r => { if (r.isConfirmed) doCancel(); });
  }
  // ==========================================================================

  // Remove main image (just clears the key)
  removeMainImage(): void {
    if (this.isAwaitingMainValidation) { this.cancelMainPreview(); return; }
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.mainImageKey = '';
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  // Remove secondary image
  removeSecondaryImage(index: number): void {
    Swal.fire({
      title: 'Retirer l\'image ?',
      text: 'Cette image sera retirée de la liste (non supprimée du cloud).',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui, retirer'
    }).then((result) => {
      if (result.isConfirmed) {
        this.secondaryImages.splice(index, 1);
        Swal.fire({ icon: 'success', title: 'Retirée', timer: 1500, showConfirmButton: false });
      }
    });
  }

  private buildPayload(): any {
    return {
      name: this.sousCategorie.name?.trim() || '',
      namearabe: this.sousCategorie.namearabe?.trim() || '',
      nameanglais: this.sousCategorie.nameanglais?.trim() || '',
      description: this.sousCategorie.description?.trim() || '',
      descriptionarabe: this.sousCategorie.descriptionarabe?.trim() || '',
      descriptionanglais: this.sousCategorie.descriptionanglais?.trim() || '',
      category: this.sousCategorie.category?.trim() || '',
      prix: this.sousCategorie.prix?.trim() || '',
      video: this.sousCategorie.video?.trim() || '',
      image_url: this.mainImageKey || '',
      listimages: [...this.secondaryImages]
    };
  }

  updateSousCat(): void {
    // NEW : block si validation en attente
    if (this.isAwaitingMainValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser l\'image principale.', confirmButtonColor: '#ff9800' });
      return;
    }
    if (this.isAwaitingSecondaryValidation) {
      Swal.fire({ icon: 'warning', title: 'Validation requise', text: 'Veuillez valider ou refuser les images secondaires.', confirmButtonColor: '#ff9800' });
      return;
    }

    this.isSubmitting = true;
    const payload = this.buildPayload();
    console.log('Payload envoyé:', payload);

    Swal.fire({ title: 'Mise à jour...', text: 'Veuillez patienter', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    if (!this.idSouCat) {
      Swal.close();
      Swal.fire({ icon: 'error', title: 'ID manquant', text: 'Impossible de mettre à jour', confirmButtonColor: '#f44336' });
      this.isSubmitting = false;
      return;
    }

    this.sousCatService.updateSousCategorie(this.idSouCat, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
        Swal.close();
        Swal.fire({ icon: 'success', title: 'Succès !', text: 'Sous-catégorie mise à jour', timer: 1500, showConfirmButton: false })
          .then(() => this.goToList());
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        Swal.close();
        let errorMessage = 'Erreur lors de la mise à jour';
        if (error.error?.message) errorMessage = error.error.message;
        Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
      }
    });
  }

  cancel(): void { this.goToList(); }

  private goToList(): void {
    if (this.produitId) {
      this.router.navigate(['/list-sous-produits', this.produitId]);
    } else {
      this.location.back();
    }
  }

  navigateBack(): void { this.cancel(); }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser ?',
      text: 'Toutes les modifications non sauvegardées seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      confirmButtonText: 'Oui',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.mainPreviewOriginalUrl = null;
        this.mainPreviewProcessedUrl = null;
        this.isAwaitingMainValidation = false;
        this.mainUploadMode = 'direct';
        this.secondaryPreviewOriginalUrls = [];
        this.secondaryPreviewProcessedUrls = [];
        this.isAwaitingSecondaryValidation = false;
        this.secondaryUploadMode = 'direct';
        this.getSousCategorieDetails();
        Swal.fire({ icon: 'success', title: 'Réinitialisé', timer: 1500, showConfirmButton: false });
      }
    });
  }
}